/**
 * Fast, rule-based "next best action" hint for a lead card.
 * Returns { text, tone } or null when there's nothing urgent to say.
 *
 * We derive it on the frontend so every card gets an instant, always-fresh
 * hint without hitting the LLM per card. If the drawer's manual AI run has
 * persisted a fresh `nba_action` on the lead, that wins.
 */

const HOURS = (ms) => ms / (1000 * 60 * 60);

export function leadNbaHint(lead) {
  if (!lead) return null;

  // 1) If the AI drawer produced one in the last 24h, honor it.
  if (lead.nba_action && lead.nba_generated_at) {
    const age = HOURS(Date.now() - new Date(lead.nba_generated_at).getTime());
    if (age < 24) {
      return { text: lead.nba_action, tone: "ai" };
    }
  }

  const mot = lead.motivation_score;
  const hasPhone = !!lead.phone;
  const hasOffer = lead.offer_amount != null && lead.offer_amount > 0;
  const stage = lead.stage;
  const lastAt = lead.last_contact_at
    ? new Date(lead.last_contact_at).getTime()
    : null;
  const daysSince = lastAt ? (Date.now() - lastAt) / 86400000 : null;

  // 2) Sizzling — high motivation, no contact yet
  if (mot != null && mot >= 80 && hasPhone && (!lastAt || daysSince > 0.5)) {
    return { text: `Call now — motivation ${mot}`, tone: "hot" };
  }

  // 3) Contract stage but no signed docs / activity in 2+ days
  if (stage === "contract" && (!daysSince || daysSince > 2)) {
    return { text: "Send / re-send DocuSign", tone: "action" };
  }

  // 4) Offer sent, silent 3+ days
  if (hasOffer && stage === "offer_sent" && daysSince != null && daysSince > 3) {
    return { text: `Follow up — offer sent ${Math.round(daysSince)}d ago`, tone: "warn" };
  }

  // 5) Warming up
  if (mot != null && mot >= 60 && mot < 80) {
    return { text: `Text follow-up — motivation ${mot}`, tone: "warm" };
  }

  // 6) Brand new & never contacted
  if (!lastAt && hasPhone && (stage === "new_lead" || !stage)) {
    return { text: "First touch — cold call", tone: "cold" };
  }

  // 7) Stale — 7+ days silent while still in active pipeline
  if (daysSince != null && daysSince > 7 &&
      ["new_lead", "warm_lead", "hot_lead", "nurture"].includes(stage)) {
    return { text: `Stale ${Math.round(daysSince)}d — re-engage`, tone: "warn" };
  }

  return null;
}

export const HINT_TONE_STYLE = {
  ai:     "bg-violet-100 text-violet-900 border-violet-300",
  hot:    "bg-red-100 text-red-900 border-red-300",
  warm:   "bg-amber-100 text-amber-900 border-amber-300",
  warn:   "bg-orange-100 text-orange-900 border-orange-300",
  action: "bg-blue-100 text-blue-900 border-blue-300",
  cold:   "bg-sky-100 text-sky-900 border-sky-300",
};
